import { SearchIcon } from '@heroicons/react/outline';
import { Trans } from '@lingui/macro';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ListWrapper } from 'src/components/lists/ListWrapper';
import { SearchInput } from 'src/components/SearchInput';
import { applyTxHistoryFilters, useTransactionHistory } from 'src/hooks/useTransactionHistory';

import { groupByDate } from './helpers';
import { HistoryFilterMenu } from './HistoryFilterMenu';
import { HistoryMobileItemLoader } from './HistoryMobileItemLoader';
import TransactionMobileRowItem from './TransactionMobileRowItem';
import { EOperationType } from './types';

export const HistoryWrapperMobile = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterQuery, setFilterQuery] = useState<EOperationType[]>([]);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchResetKey, setSearchResetKey] = useState(0);

  const handleCancelClick = () => {
    setShowSearchBar(false);
    setSearchQuery('');
  };

  // Create ref to exclude search box from click handler below
  const searchBarRef = useRef<HTMLElement | null>(null);

  // Close search bar if it's open, has a blank query, and the clicked item is not the search box
  const handleClickOutside = (event: MouseEvent) => {
    if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
      if (searchQuery === '' && showSearchBar) {
        handleCancelClick();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, showSearchBar]);

  const {
    data: transactions,
    isLoading,
  } = useTransactionHistory();

  const filteredTxns = useMemo(
    () => applyTxHistoryFilters({ searchQuery, filterQuery, txns: transactions }),
    [searchQuery, filterQuery, transactions]
  );
  const isEmpty = filteredTxns.length === 0;
  const filterActive = searchQuery !== '' || filterQuery.length > 0;

  return (
    <ListWrapper
      wrapperSx={showSearchBar ? { px: 4 } : undefined}
      titleComponent={
        <Box
          ref={searchBarRef}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          {!showSearchBar && (
            <Typography component="div" variant="h2" sx={{ mr: 4, height: '36px' }}>
              <Trans>Transactions</Trans>
            </Typography>
          )}
          {!showSearchBar && (
            <Box sx={{ display: 'flex', gap: '22px' }}>
              <Box onClick={() => setShowSearchBar(true)}>
                <SvgIcon sx={{ cursor: 'pointer' }}>
                  <SearchIcon width={20} height={20} />
                </SvgIcon>
              </Box>
            </Box>
          )}
          {showSearchBar && (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', px: 0 }}>
              <SearchInput
                wrapperSx={{
                  width: '320px',
                }}
                placeholder="Search assets..."
                onSearchTermChange={setSearchQuery}
                key={searchResetKey}
              />
              <Button onClick={() => handleCancelClick()}>
                <Typography variant="buttonM">
                  <Trans>Cancel</Trans>
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
      }
    >
      <HistoryFilterMenu onFilterChange={setFilterQuery} currentFilter={filterQuery} />

      {isLoading ? (
        <>
          <HistoryMobileItemLoader />
          <HistoryMobileItemLoader />
        </>
      ) : !isEmpty ? (
        Object.entries(groupByDate(filteredTxns)).map(([date, txns], groupIndex) => (
          <React.Fragment key={groupIndex}>
            <Typography variant="h4" color="text.primary" sx={{ ml: 4, mt: 6, mb: 2 }}>
              {date}
            </Typography>
            {txns.map((transaction, index: number) => {
              return (
                <div ref={null} key={index}>
                  <TransactionMobileRowItem
                    transaction={transaction}
                  />
                </div>
              );
            })}
          </React.Fragment>
        ))
      ) : filterActive ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flex: 1,
            maxWidth: '468px',
            margin: '0 auto',
            my: 24,
          }}
        >
          <Typography variant="h3" color="text.primary">
            <Trans>Nothing found</Trans>
          </Typography>
          <Typography sx={{ mt: 1, mb: 4 }} variant="description" color="text.secondary">
            <Trans>
              We couldn&apos;t find any transactions related to your search. Try again with a
              different asset name, or reset filters.
            </Trans>
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchQuery('');
              setFilterQuery([]);
              setSearchResetKey((prevKey) => prevKey + 1); // Remount SearchInput component to clear search query
            }}
          >
            Reset Filters
          </Button>
        </Box>
      ) : (
        <></>
      )}
    </ListWrapper>
  );
};
